// React
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// User Actions
import { getUserDetails } from "../../actions/userActions";

import { isDayComplete, sortDaysByLogDate } from "../../utils/dayProgress";

import {
  listDays,
  createDayAction,
  patchDayAction,
  getDayByIdAction,
} from "../../actions/dayActions";

// Multi-device sync — per-field dirty tracking + durable flush queue.
// Replaces the previous "debounce a full-blob PUT off every keystroke"
// approach that silently lost edits made on a second device.
import {
  markDirty,
  flushNow,
  registerSender,
  installUnloadHooks,
  getPending,
  discardFields,
  getOrCreateDeviceId,
} from "../../utils/syncQueue";

// Stage 3 live-broadcast layer.
import {
  subscribe as socketSubscribe,
  unsubscribe as socketUnsubscribe,
  onPatch as socketOnPatch,
  onReconnect as socketOnReconnect,
  getSocketId,
} from "../../services/socket";

import { DAYS_PATCH_SUCCESS } from "../../constants/daysConstants";

import {
  createWeekAction,
  updateWeekAction,
  getWeekbyIdAction,
} from "../../actions/weekActions";

import { getYearByIdAction } from "../../actions/yearActions";

// Internal Components

// Hooks
import useResponsiveState from "../../hooks/useResponsiveState";
import { usePersistentLoading } from "../../hooks/usePersistentLoading";
import { useLoadingContent } from "../../hooks/useLoadingContent";
import { useLoadingFloor } from "../../hooks/useLoadingFloor";
import useObjectivesState from "../../features/objectives/hooks/useObjectivesState";

// Objectives
import Board from "../../features/objectives/Board";

//// Layout
import TileContainer from "./features/tiles/TileContainer";
import Header from "../../components/layouts/Header/Header";
import Footer from "../../components/layouts/Footer/TestFooter";
import PageContainer from "../../components/layouts/Containers/PageContainer";
import FloatingInfoPanel from "../../components/ui/FloatingInfoPanel/FloatingInfoPanel";
import { LoadingTile } from "../../components/ui/Loading/LoadingTile";
import HowItWorksModal from "../../components/ui/HowItWorksModal/HowItWorksModal";
import SaveStatusIndicator from "../../components/ui/SaveStatus/SaveStatusIndicator";
import AboutYouCard from "../../components/ui/AboutYouCard/AboutYouCard";

import "./dayscreens.css";
import SEO from "../../components/seo/SEO";

function DayScreen({ history }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { viewType, data } = useResponsiveState();

  const loadingContent = useLoadingContent();
  const pageLoading = useLoadingFloor(10000, loadingContent);
  const { isShowing, startLoading, stopLoading, tileProps } =
    usePersistentLoading(pageLoading, loadingContent);

  // Hooks
  const yearList = useSelector((state) => state.yearList);
  const yearItem = yearList.years?.at(-1);

  const yearSingle = useSelector((state) => state.yearSingle);

  const dayList = useSelector((state) => state.dayList);
  const { days } = dayList;

  const daySingle = useSelector((state) => state.daySingle);
  const { day, loading } = daySingle;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const weekList = useSelector((state) => state.weekList.weeks?.at(-1));

  const weekSingle = useSelector((state) => state.weekSingle?.week);

  const isCurrentlyFetching = loading;

  useEffect(() => {
    // This hook now directly reflects the Redux state.
    if (isCurrentlyFetching) {
      startLoading();
    } else {
      stopLoading();
    }
  }, [isCurrentlyFetching, startLoading, stopLoading]);

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  // Fetch user data
  useEffect(() => {
    dispatch(listDays());
  }, [dispatch, userInfo]);

  useEffect(() => {
    if (id) {
      dispatch(getDayByIdAction(id));
    } else {
      setToday({});
      setPageState("Create Day");
      setBackground({});
      setTooltipPosition(undefined);
    }
  }, [id, userInfo]);

  useEffect(() => {
    if (yearList && yearList.years && yearItem)
      dispatch(getYearByIdAction(yearItem._id));
  }, [yearList, yearItem, userInfo]);

  useEffect(() => {
    if (weekList && weekList._id) dispatch(getWeekbyIdAction(weekList._id));
  }, [weekList, userInfo]);

  // One-time "How it works" modal for users who haven't seen it yet
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  useEffect(() => {
    const hasSeen = localStorage.getItem("nwd_how_it_works_seen");
    if (!hasSeen) {
      localStorage.setItem("nwd_how_it_works_seen", "true");
      setShowHowItWorks(false);
    }
  }, []);

  // State
  //// Data
  const [today, setToday] = useState(day || {});
  const [pendingConsumed, setPendingConsumed] = useState(false);

  // Consume a "pending day" handed off from the landing-page demo. The CTA on
  // / saves localStorage("nwd_pending_day") and routes through /register; on
  // first /today visit (no id) we create a day from that payload.
  useEffect(() => {
    if (id || pendingConsumed || !userInfo) return;
    const raw =
      typeof window !== "undefined"
        ? window.localStorage.getItem("nwd_pending_day")
        : null;
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch (e) {
      window.localStorage.removeItem("nwd_pending_day");
      return;
    }
    window.localStorage.removeItem("nwd_pending_day");
    setPendingConsumed(true);
    setToday(payload);
    dispatch(createDayAction(payload))
      .then((response) => {
        if (response && response._id) navigate(`/today/${response._id}`);
      })
      .catch((error) => console.error("Pending day create failed:", error));
  }, [id, userInfo, pendingConsumed, dispatch, navigate]);
  const [latestYear, setLatestYear] = useState();
  const {
    objectives,
    columnOrder,
    setObjectives,
    setColumnOrder,
    changeObjectiveTitle,
    deleteObjective,
    reorderObjectives,
    handleSort,
    onDragEnd,
    addSubTask,
    toggleCompletedSubTasks,
    handleEstimateChange,
    changeSubTaskUrgency,
    updateDescription,
    updateNotes,
    deleteSubtask,
    addStep,
    toggleStep,
    updateStep,
    deleteStep,
    onDragEndStep,
  } = useObjectivesState();
  const [meyear, setMeyear] = useState();

  // MouseTooltip
  const [mouseState, setMouseState] = useState("");
  const [cursorDisplayState, setCursorDisplayState] = useState();

  //// Layout
  const [viewWidth, setViewWidth] = useState(
    Math.round(Math.max(document.documentElement.clientWidth)),
  );
  const [viewHeight, setViewHeight] = useState(
    Math.round(Math.max(document.documentElement.clientHeight)),
  );
  const [tooltipPosition, setTooltipPosition] = useState();
  const [background, setBackground] = useState({});
  const [continuation, setContinuation] = useState();
  const [cornerState, setCornerState] = useState({});
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [calloutActive, setCalloutActive] = useState(false);
  const [weekActivated, setWeekActivated] = useState(false);

  ////
  const [columnState, setColumnState] = useState(false);
  const [load, setLoad] = useState("");
  const [pageState, setPageState] = useState(id ? "Update Day" : "Create Day");

  const [displayState, setDisplayState] = useState({
    "obj-one": true,
    "obj-two": false,
    "obj-three": false,
    "obj-four": false,
    "obj-five": false,
    "obj-six": false,
    "obj-seven": false,
    "obj-eight": false,
    "obj-nine": false,
    "obj-ten": false,
  });

  const handleColumnChange = (e) => {
    let data = e.currentTarget.dataset;
    let newColumn = data.name;

    setDisplayState((displayState) => {
      let shallow = { ...displayState };

      Object.keys(shallow).map((el) => {
        if (el === newColumn) {
          shallow[el] = true;
        } else {
          shallow[el] = false;
        }
      });

      return shallow;
    });

    setColumnState(!columnState);
  };

  // Functions
  //// Data Transformations
  useEffect(() => {
    if (day && Object.keys(day).length > 0) {
      setToday(day);
    }
  }, [day]);

  const dayIsComplete = useMemo(() => isDayComplete(today), [today]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("nwd:day-validation", {
        // On the blank new-day form there is no day to finish, so nothing should
        // be gated. Broadcasting `false` here was telling the user to "complete
        // your day to start a new one" while they stood on the new-day form.
        detail: { canCreateDay: id ? dayIsComplete : true },
      }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("nwd:day-validation", {
          detail: { canCreateDay: null },
        }),
      );
    };
  }, [dayIsComplete, id]);

  useEffect(() => {
    if (!weekSingle) {
      setColumnOrder(Object.keys(objectives));
    } else {
      setObjectives(weekSingle.objectives);
      setColumnOrder(weekSingle.columnOrder);
      setWeekActivated(true);
    }
  }, [weekSingle]);

  useEffect(() => {
    if (yearSingle && yearSingle.year) {
      setMeyear((meyear) => {
        if (yearSingle.year.values) {
          return {
            obj: yearSingle.year,
            values_arr: Object.keys(yearSingle.year.values),
          };
        } else {
          return {
            obj: yearSingle.year,
            values_arr: [],
          };
        }
      });
    }
  }, [yearSingle]);

  const closeTooltip = () => {
    setCursorDisplayState(false);
  };

  const handleSliderChange = (event, num, nam) => {
    const sliderValue = event.target.value;
    const sliderName = event.target.name;

    // Calculate the tooltip position based on the slider value
    const sliderWidth = event.target.clientWidth - 15;

    const newPercent = sliderValue / num;
    const newPosition = sliderWidth * newPercent;

    setTooltipPosition((prevState) => {
      let shallowCopy = { ...prevState };

      if (!shallowCopy[sliderName]) {
        // Add to existing state
        shallowCopy[sliderName] = { position: newPosition + 20, opacity: 1 };
      } else {
        // Copy previous state, change individual obj,
        shallowCopy[sliderName] = { position: newPosition + 20, opacity: 1 };
      }

      return shallowCopy;
    });

    const percent = (sliderValue / num) * 100;

    setBackground({
      ...background,
      [sliderName]: `linear-gradient(to right, rgba(0, 74, 35, 0.5) ${percent}%, transparent ${percent}%)`,
    });
  };

  const startSlider = (value, num) => {
    const sliderValue = value;

    const percent = (sliderValue / num) * 100;

    return `linear-gradient(to right, rgba(0, 74, 35, 0.5) ${percent}%, transparent ${percent}%)`;
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setToday((prevToday) => ({
        ...prevToday,
        [name]: value,
      }));

      if (name === "startScore" || name === "endScore") {
        handleSliderChange(e, 5);
      }

      if (name.startsWith("mom")) {
        handleSliderChange(e, 10);
      }

      // Per-field dirty marker for multi-device sync. Once the day exists
      // on the server (id present), the syncQueue will debounce, coalesce,
      // and PATCH only this field — not the whole `today` blob.
      if (id) markDirty("day", id, name, value);
    },
    [handleSliderChange, id],
  );

  const promoteStepToMoment = useCallback(
    (momentKey, stepText) => {
      if (!stepText) return;
      setToday((prevToday) => {
        const existing = prevToday[momentKey] || "";
        const nextValue = existing ? `${existing}\n${stepText}` : stepText;
        if (id) markDirty("day", id, momentKey, nextValue);
        return { ...prevToday, [momentKey]: nextValue };
      });
    },
    [id],
  );

  // Each successful PATCH calls handleSave to pulse the dock save icon
  // green for 2s. Continuous typing fires PATCHes every ~500ms, so we
  // must cancel any previously-scheduled clear before scheduling a new
  // one — otherwise orphaned timers fire between successful saves and
  // make the icon flicker.
  const saveIconTimerRef = useRef(null);
  const handleSave = useCallback(() => {
    setLoad("success");
    if (saveIconTimerRef.current) clearTimeout(saveIconTimerRef.current);
    saveIconTimerRef.current = setTimeout(() => {
      setLoad("");
      saveIconTimerRef.current = null;
    }, 2000);
  }, []);
  useEffect(
    () => () => {
      if (saveIconTimerRef.current) clearTimeout(saveIconTimerRef.current);
    },
    [],
  );

  const handleColumnClose = (e) => {
    let name = e.target.dataset.name;

    if (name === "background") {
      setColumnState(false);
    }
  };

  // Board Functions

  const handleOverlayChange = (newState) => {
    setOverlayVisible(newState);
  };

  const deleteTodayKey = (e) => {
    let data = e.currentTarget.dataset;
    let name = data.name;

    const scoreKeyByMoment = {
      momentOne: "momentOneScore",
      momentTwo: "momentTwoScore",
      momentThree: "momentThreeScore",
      momentFour: "momentFourScore",
      momentFive: "momentFiveScore",
    };
    const scoreKey = scoreKeyByMoment[name];

    setToday((prevDay) => {
      let shallow = { ...prevDay };
      delete shallow[name];
      if (scoreKey) delete shallow[scoreKey];
      return shallow;
    });

    // The server PATCH endpoint sets fields rather than unsetting them, so
    // we represent "cleared" as an empty string for the text and 0 for the
    // accompanying score. The UI hides moments whose text is falsy, so the
    // visual outcome matches the previous delete-the-key behaviour.
    if (id) {
      markDirty("day", id, name, "");
      if (scoreKey) markDirty("day", id, scoreKey, 0);
    }

    setBackground((prevState) => {
      let shallow = { ...prevState };

      delete shallow[`${name}`];
      delete shallow[`${name}Score`];

      return shallow;
    });

    setTooltipPosition((prevState) => {
      let shallow = { ...prevState };

      delete shallow[`${name}`];
      delete shallow[`${name}Score`];

      return shallow;
    });
  };

  const handleWeekSubmit = async () => {
    let fal = false;
    if (id === undefined && fal === true) {
      dispatch(createWeekAction(objectives, columnOrder))
        .then(async (response) => {
          if (response) {
            let nextPage = await response._id;
            // navigate(`/week/${nextPage}`);
          } else {
            console.log("Error");
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      dispatch(updateWeekAction(weekList._id, objectives, columnOrder))
        .then(async (response) => {
          let r = await response;
          // console.log(r);
          // handleSave();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  useEffect(() => {
    if (weekActivated && weekSingle) {
      if (!pageLoading) {
        handleWeekSubmit();
      }
    }
  }, [objectives]);

  const handleCalloutDisplay = () => {
    setCalloutActive((calloutActive) => !calloutActive);
  };

  // Remote patches that landed for fields the user is currently editing.
  // Surfaced via a banner; the user must opt in to overwrite their typing.
  const [incomingPending, setIncomingPending] = useState({});

  // Wire syncQueue to Redux once: future PATCHes are dispatched through
  // patchDayAction so the daySingleReducer can absorb the new version /
  // fieldMeta from each server response. Each PATCH carries the
  // server-assigned socketId so the broadcast layer can suppress the
  // originating device's own echo.
  useEffect(() => {
    registerSender("day", async (recordId, payload) => {
      try {
        const enriched = { ...payload, socketId: getSocketId() };
        const response = await dispatch(patchDayAction(recordId, enriched));
        handleSave();
        return response;
      } catch (err) {
        setLoad("error");
        throw err;
      }
    });
    installUnloadHooks(process.env.REACT_APP_API_URL);
  }, [dispatch]);

  // Flush any pending edits when the user navigates to a different day or
  // unmounts the screen — guarantees the previous day's keystrokes don't
  // sit in localStorage waiting for the next visit.
  useEffect(() => {
    if (!id) return;
    return () => {
      flushNow("day", id).catch(() => {});
    };
  }, [id]);

  // Subscribe to the current day's broadcast room and handle inbound
  // patches from other devices. Non-dirty fields update silently; dirty
  // fields are queued in `incomingPending` and surface via the banner so
  // the user's keystrokes are never overwritten mid-typing.
  useEffect(() => {
    if (!id) return undefined;
    socketSubscribe("day", id);

    const offPatch = socketOnPatch((msg) => {
      if (!msg || msg.type !== "day" || msg.id !== id) return;
      // Self-suppress by deviceId. socketId-based exclusion on the server
      // fails when the socket hadn't connected yet at PATCH-flush time
      // (getSocketId() returned null), which would otherwise surface our
      // own echo as a phantom conflict banner.
      if (msg.originDeviceId && msg.originDeviceId === getOrCreateDeviceId())
        return;
      const incoming = msg.patches || {};
      const dirty = getPending("day", id);
      const conflicting = {};

      setToday((prev) => {
        const next = { ...prev };
        for (const [field, value] of Object.entries(incoming)) {
          if (dirty[field]) {
            conflicting[field] = value;
          } else {
            next[field] = value;
          }
        }
        return next;
      });

      if (Object.keys(conflicting).length > 0) {
        setIncomingPending((prev) => ({ ...prev, ...conflicting }));
      }

      // Advance our optimistic-lock cursor so the next outgoing PATCH
      // carries the up-to-date version/fieldMeta.
      if (typeof msg.version === "number" || msg.fieldMeta) {
        dispatch({
          type: DAYS_PATCH_SUCCESS,
          payload: { version: msg.version, fieldMeta: msg.fieldMeta },
        });
      }
    });

    // After a reconnect we may have missed broadcasts. Refetch the
    // current day rather than asking the server to replay events.
    const offReconnect = socketOnReconnect(() => {
      dispatch(getDayByIdAction(id));
    });

    return () => {
      offPatch();
      offReconnect();
      socketUnsubscribe("day", id);
    };
  }, [id, dispatch]);

  // User clicked "show me the other device's changes". Apply the buffered
  // remote patches to local state and drop our queued local edits for
  // those fields so the syncQueue does not push them back.
  const acceptIncomingChanges = useCallback(() => {
    if (Object.keys(incomingPending).length === 0) return;
    const fields = Object.keys(incomingPending);
    setToday((prev) => ({ ...prev, ...incomingPending }));
    discardFields("day", id, fields);
    setIncomingPending({});
  }, [incomingPending, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (id === undefined) {
        // Create a new day
        await dispatch(createDayAction(today))
          .then(async (response) => {
            if (response) {
              let nextPage = await response._id;
              navigate(`/today/${nextPage}`);
              // console.log(nextPage);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        // Force-flush any pending field edits — markDirty has already
        // queued the user's changes; this just bypasses the 500ms debounce
        // for the manual submit affordance.
        flushNow("day", id).catch(() => {});
      }
    } catch (error) {
      // Handle errors from action creators
      console.error("Error in handleSubmit:", error.message);
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  useEffect(() => {
    const onNavSave = () => handleSubmitRef.current({ preventDefault() {} });
    window.addEventListener("nwd:save", onNavSave);
    return () => window.removeEventListener("nwd:save", onNavSave);
  }, []);

  // The day before the one on screen, by date.
  //
  // This had two bugs: it guarded on `at(-2)` but returned `at(-1)`, i.e. the
  // day you were already looking at, and it returned a relative "today/<id>",
  // which from /today/:id resolved to /today/today/<id> and rendered nothing.
  const previousDay = () => {
    const ordered = sortDaysByLogDate(dayList?.days, "asc");
    const cursor = id ? ordered.findIndex((d) => d.id === id) : ordered.length;
    if (cursor <= 0) return undefined;
    return `/today/${ordered[cursor - 1].id}`;
  };

  const handleMouseState = (str) => {
    if (str === mouseState) {
      setCursorDisplayState(false);
    } else {
      setMouseState(str);
      setCursorDisplayState(true);
    }
  };

  const handleTodayAiChange = (data) => {
    setToday((prevToday) => {
      let copy = { ...prevToday };

      let transcribe_keys = Object.keys(data);
      let today_keys = Object.keys(copy);

      if (today_keys.length === 0) {
        copy = data;
      } else {
        for (let i = 0; i < transcribe_keys.length; i++) {
          for (let j = 0; j < today_keys.length; j++) {
            if (today_keys[j] !== transcribe_keys[i]) {
              copy[transcribe_keys[i]] = data[transcribe_keys[i]];
            }
          }
        }
      }

      return copy;
    });
  };

  const hasIncoming = Object.keys(incomingPending).length > 0;

  return (
    <>
      <SaveStatusIndicator recordType="day" />
      {hasIncoming && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            background: "rgba(0, 74, 35, 0.95)",
            color: "white",
            padding: "10px 16px",
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>Updated on another device</span>
          <button
            type="button"
            onClick={acceptIncomingChanges}
            style={{
              background: "white",
              color: "rgb(0, 74, 35)",
              border: "none",
              padding: "4px 10px",
              borderRadius: 4,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Apply
          </button>
        </div>
      )}
      {showHowItWorks && (
        <HowItWorksModal
          mode="reference"
          onClose={() => setShowHowItWorks(false)}
        />
      )}
      <SEO
        title={pageState}
        description="Complete and create your journal entry"
        canonicalUrl={`https://www.nowasteddays.io/today`}
      />
      <Header variant="light" active={true} />
      <FloatingInfoPanel
        mouseState={mouseState}
        handleMouseState={handleMouseState}
        meyear={meyear}
        cursorDisplayState={cursorDisplayState}
        viewType={viewType}
        closeTooltip={closeTooltip}
      />
      {/* Sits above the journal, not inside it: .updateday--container is a
          flex row on desktop, so a child in there takes a whole column's worth
          of width from the entries. Renders itself only once the practice has
          taken hold and the questions are still outstanding; silent otherwise. */}
      <AboutYouCard />
      <PageContainer
        className="updateday--container"
        style={
          viewType === "desktop" && viewWidth < 1300
            ? {
                height: `${viewHeight - 72}px`,
                maxHeight: `${viewHeight - 72}px`,
              }
            : undefined
        }
      >
        {isShowing ? (
          <LoadingTile content={loadingContent} {...tileProps} />
        ) : (
          <>
            {columnState && objectives && (
              <Board
                day={true}
                initial={objectives}
                ordered={columnOrder}
                overlay={isOverlayVisible}
                onDragEnd={onDragEnd}
                onDragEndSteps={onDragEndStep}
                handleUrgencyChange={changeSubTaskUrgency}
                handleEstimateChange={handleEstimateChange}
                handleColumnChange={handleColumnClose}
                handleSort={handleSort}
                handleWeekSubmit={handleWeekSubmit}
                yearList={yearSingle.year}
                display={displayState}
                addSubTask={addSubTask}
                addStep={addStep}
                toggleSubTask={toggleCompletedSubTasks}
                toggleStep={toggleStep}
                toggleDisplay={handleCalloutDisplay}
                toggleOverlay={handleOverlayChange}
                updateTitle={changeObjectiveTitle}
                updateOrder={reorderObjectives}
                updateDescription={updateDescription}
                updateStep={updateStep}
                updateNotes={updateNotes}
                deleteObjective={deleteObjective}
                deleteStep={deleteStep}
                deleteSubtask={deleteSubtask}
                promoteStepToMoment={promoteStepToMoment}
                momentValues={{
                  momentOne: today.momentOne,
                  momentTwo: today.momentTwo,
                  momentThree: today.momentThree,
                  momentFour: today.momentFour,
                  momentFive: today.momentFive,
                }}
                viewWidth={viewWidth}
                viewType={viewType}
                withScrollableColumns
              />
            )}
            <TileContainer
              background={background}
              continuation={continuation}
              objectives={objectives}
              today={today}
              meyear={meyear}
              yearid={yearItem}
              yearList={yearList}
              dayList={dayList}
              load={load}
              tooltipPosition={tooltipPosition}
              startSlider={startSlider}
              handleChange={handleChange}
              handleMouseState={handleMouseState}
              handleColumnChange={handleColumnChange}
              handleSubmit={handleSubmit}
              handleTodayAiChange={handleTodayAiChange}
              deleteTodayKey={deleteTodayKey}
              previousDay={previousDay()}
              closeTooltip={closeTooltip}
            />
          </>
        )}
      </PageContainer>
      <Footer />
    </>
  );
}

export default DayScreen;
